import React from "react";
import {
  LinkRecordFull,
  EmbeddedStatus,
  getAppLogoFromLinkRecord,
} from "winslow-core";
import { MyTooltip } from "../common/MyTooltip";
import { PersonaChips } from "./PersonaChips";
import { ResourceOwner } from "./ResourceOwner";
import { ResourceActions } from "./ResourceActions";
import config from "environment";
import { styled } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
import {
  selectUIStates,
  selectUser,
  setUIStates,
} from "../../redux/features/appData";
import { selectSupportedApps } from "../../redux/features/connectedApps/connectedAppsSlice";

const PersonaChipsPlaceholder = styled("div")({
  minHeight: "32px", // Standard height of chip components
  width: "100%",
  gridColumn: "span 2", // Match the grid span of PersonaChips
  margin: "8px 0", // Maintain consistent spacing
});

interface ResourceCardProps {
  linkRecord: LinkRecordFull;
  onDelete: () => void;
  onOpen: () => void;
  onFolderDelete?: () => void;
  personas: any[];
  personaLinkRecords: any;
  onAddPersona: (personaId: string) => void;
  onRemovePersona: (personaId: string) => void;
  onTogglePersonaOrientation: (personaId: string) => void;
  showPersonaSelector: string | null;
  setShowPersonaSelector: (showPersonaSelector: string | null) => void;
  openResourceActionSidebar: (linkRecordId: string) => void;
}

export const ResourceCard: React.FC<ResourceCardProps> = ({
  linkRecord,
  onDelete,
  onOpen,
  onFolderDelete,
  personas,
  personaLinkRecords,
  onAddPersona,
  onRemovePersona,
  onTogglePersonaOrientation,
  showPersonaSelector,
  setShowPersonaSelector,
  openResourceActionSidebar,
}) => {
  const isNotEmbedded = (): boolean => {
    return linkRecord.embeddedStatus !== EmbeddedStatus.PROCESSED;
  };

  const dispatch = useDispatch();
  const uiStates = useSelector(selectUIStates);

  const storeUser = useSelector(selectUser);
  const supportedApps = useSelector(selectSupportedApps);

  const determineToolTip = (): string => {
    if (linkRecord.embeddedFailedReason) {
      return linkRecord.embeddedFailedReason;
    }

    switch (linkRecord.embeddedStatus) {
      case EmbeddedStatus.PROCESSED:
        return "Processed";
      case EmbeddedStatus.FORMAT_NOT_SUPPORTED:
        return "Format not supported or content not available";
      case EmbeddedStatus.SYSTEM_OUTAGE:
        return "System outage - please remove and try again";
      case EmbeddedStatus.TOO_LARGE:
        return "This document is too large to process";
      case EmbeddedStatus.NOT_PROCESSED:
      case EmbeddedStatus.UNKNOWN:
      default:
        return "This document is not available for questions";
    }
  };

  const folderName = linkRecord.folderName;
  const fileCount = linkRecord.folderSlug
    ? personaLinkRecords[linkRecord.folderSlug]?.length || 0
    : 0;

  const handleOpenResourceActionSidebar = () => {
    openResourceActionSidebar(linkRecord.id);
  };

  return (
    <div
      className={`collection-resources-record ${isNotEmbedded() ? "not-embedded" : ""}`}>
      <div className="collection-resources-icon">
        {linkRecord.links && linkRecord.links.length > 0 && (
          <>
            <img
              src={
                "/icons/" +
                storeUser?.theme +
                "/app_logos/" +
                getAppLogoFromLinkRecord(
                  linkRecord,
                  supportedApps ? supportedApps : [],
                  "-activity.svg"
                )
              }
              alt=""
            />
          </>
        )}
      </div>

      <div className="collection-resources-fileinfo">
        <div className="collection-resources-title">
          {linkRecord.links?.[0]?.title}
        </div>
        {linkRecord.links?.[0] &&
          linkRecord.source === "LINK" &&
          (linkRecord.links[0].appId === "OTHER" ||
            linkRecord.links[0].appId === "LINK_PDF") && (
            <div className="collection-resources-url-card-url">
              <a href={linkRecord.links[0].url} target="_blank">
                {linkRecord.links[0].url}
              </a>
            </div>
          )}
        {linkRecord.folderName && (
          <div className="collection-resources-subfolder">
            <MyTooltip title={<span>{linkRecord.folderName}</span>}>
              <>
                in {linkRecord.siteName ? `${linkRecord.siteName}>` : ""}
                {linkRecord.folderName}
              </>
            </MyTooltip>
          </div>
        )}
      </div>
      {config.showPersonas ? (
        <PersonaChips
          linkRecordId={linkRecord.id}
          personas={personas}
          personaLinkRecords={personaLinkRecords}
          onAddPersona={onAddPersona}
          onRemovePersona={onRemovePersona}
          onToggleOrientation={onTogglePersonaOrientation}
          showSelector={showPersonaSelector === linkRecord.id}
          onShowSelector={() =>
            setShowPersonaSelector(
              showPersonaSelector === linkRecord.id ? null : linkRecord.id
            )
          }
        />
      ) : (
        <PersonaChipsPlaceholder />
      )}

      {linkRecord.user &&
        linkRecord.user.firstName &&
        linkRecord.user.lastName && (
          <ResourceOwner
            avatarUrl={linkRecord.user.avatarURL}
            firstName={linkRecord.user.firstName}
            lastName={linkRecord.user.lastName}
            createdAt={linkRecord.createdAt}
          />
        )}

      <ResourceActions
        isEmbedded={!isNotEmbedded()}
        tooltip={determineToolTip()}
        onOpen={onOpen}
        onDelete={onDelete}
        onFolderDelete={onFolderDelete}
        folderName={folderName}
        fileCount={fileCount}
        openResourceActionSidebar={handleOpenResourceActionSidebar}
      />
    </div>
  );
};
